import "./style.css";
import { Renderer, Camera, Transform, Plane } from "ogl";
import Media from "./Media";
import HugeText from "./HugeText";
import { lerp } from "@utils/math";
import { getRaf, getScroll } from "@app";
import { laptop } from "@app/breakpoints";

export default class Canvas {
  constructor(preloadedResources) {
    this.preloadedResources = preloadedResources;

    this.raf = getRaf();
    this.isActive = false;

    this.scroll = getScroll();
    this.scroll.current = window.scrollX;
    this.scroll.target = this.scroll.current;
    this.scroll.last = this.scroll.current;
    this.scroll.speed = 0;

    this.createRenderer();
    this.createCamera();
    this.createScene();
    this.createGallery();

    this.onResize();

    this.createGeometry();
    this.createMedias();
    this.createHugeTexts();

    this.scroll.registerOnScrollPositionChange(this.onScrollPositionChanged);
  }

  createGallery() {
    this.gallery = document.querySelector(".content");
  }

  createRenderer = () => {
    this.renderer = new Renderer({
      alpha: true,
      dpr: window.devicePixelRatio,
      antialiasing: true,
      premultipliedAlpha: true,
    });

    this.gl = this.renderer.gl;

    document.body.appendChild(this.gl.canvas);
  };

  createCamera = () => {
    this.camera = new Camera(this.gl);
    this.camera.fov = 45;
    this.camera.position.z = 5;
  };

  createScene = () => {
    this.scene = new Transform();
  };

  createGeometry() {
    this.planeGeometry = new Plane(this.gl, {
      widthSegments: 10,
    });
  }

  createMedias() {
    this.imageNodes = document.querySelectorAll(".image");
    this.medias = Array.from(this.imageNodes).map((node, index) => {
      let media = new Media({
        node,
        geometry: this.planeGeometry,
        gl: this.gl,
        scene: this.scene,
        screen: this.screen,
        viewport: this.viewport,
        index,
      });

      return media;
    });
  }
  createHugeTexts() {
    this.hugeTextsNodes = document.querySelectorAll(".huge-text");
    this.hugeTexts = Array.from(this.hugeTextsNodes).map(
      (node, index) =>
        new HugeText({
          node,
          gl: this.gl,
          renderer: this.renderer,
          scene: this.scene,
          screen: this.screen,
          viewport: this.viewport,
          index,
          preloadedResources: this.preloadedResources,
        })
    );
  }

  onScrollPositionChanged = (args) => {
    const { velocity, x } = args;
    this.scroll.x = x;
    this.scroll.target = x;
    this.scroll.speed = velocity;
    this.scroll.isChanged = true;
    this.scroll.current = this.scroll.target;

    // to avoid delay between scroll and render, we render on scroll
    if (this.medias) {
      this.medias.forEach((media) => media.update(this.scroll));
    }

    if (this.hugeTexts) {
      this.hugeTexts.forEach((hugeText) => hugeText.update(this.scroll));
    }

    this.renderer.render({
      scene: this.scene,
      camera: this.camera,
    });

    // update scroll last position
    this.scroll.last = this.scroll.current;
  };

  render = (_) => {
    if (this.scroll.isChanged) {
      this.scroll.isChanged = false;

      // skip render if scroll has changed, since it will be rendered on scroll
      return;
    }

    if (this.medias) {
      this.medias.forEach((media) => media.update(this.scroll));
    }

    if (this.hugeTexts) {
      this.hugeTexts.forEach((hugeText) => hugeText.update(this.scroll));
    }

    this.renderer.render({
      scene: this.scene,
      camera: this.camera,
    });
  };

  onResize = () => {
    return new Promise((resolve) => {
      this.screen = {
        height: window.innerHeight,
        width: window.innerWidth,
      };

      this.renderer.setSize(this.screen.width, this.screen.height);

      this.camera.perspective({
        aspect: this.gl.canvas.width / this.gl.canvas.height,
      });

      const fov = this.camera.fov * (Math.PI / 180);
      const height = 2 * Math.tan(fov / 2) * this.camera.position.z;
      const width = height * this.camera.aspect;

      this.viewport = {
        height,
        width,
      };

      this.scroll.current = this.scroll.target;
      this.scroll.last = this.scroll.current;

      if (this.medias) {
        this.medias.forEach((media) =>
          media.onResize({
            screen: this.screen,
            viewport: this.viewport,
          })
        );
      }

      if (this.hugeTexts) {
        this.hugeTexts.forEach((hugeText) =>
          hugeText.onResize({
            screen: this.screen,
            viewport: this.viewport,
          })
        );
      }

      if (this.isActive && this.screen.width < laptop) {
        this.isActive = false;
        this.raf.unregister("canvas", this.render);
      } else if (!this.isActive && this.screen.width >= laptop) {
        this.isActive = true;
        this.raf.register("canvas", this.render);
      }

      resolve();
    });
  };
}
